
import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  Divider,
  Modal,
  IconButton,
} from "@mui/material";
import { LocationOn } from "@mui/icons-material";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";

const Syndicate_Card = ({ name, address, price, id, image, amenity, area, nearby, soldStatus, sold_status }) => {
  const isSold = soldStatus === 1 || sold_status === 1;
  const navi = useNavigate();
  const [imgOpen, setImgOpen] = useState(false);

  const safeAmenity = Array.isArray(amenity) ? amenity : [];
  const safeNearBy = Array.isArray(nearby) ? nearby : [];

  const limitToThree = (arr) => Array.isArray(arr) ? arr.slice(0, 3) : [];
  const amenityList = limitToThree(safeAmenity);
  const nearByList = limitToThree(safeNearBy);

  return (
    <>
      {/* IMAGE POPUP MODAL */}
      <Modal
        open={imgOpen}
        onClose={() => setImgOpen(false)}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1400 }}
      >
        <Box
          sx={{
            position: "relative",
            maxWidth: "90vw",
            maxHeight: "90vh",
            outline: "none",
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 24px 60px rgba(0,0,0,0.5)",
            animation: "popIn 0.22s cubic-bezier(0.34,1.56,0.64,1)",
            "@keyframes popIn": {
              "0%": { transform: "scale(0.7)", opacity: 0 },
              "100%": { transform: "scale(1)", opacity: 1 },
            },
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            onClick={() => setImgOpen(false)}
            sx={{ position: "absolute", top: 8, right: 8, bgcolor: "rgba(0,0,0,0.55)", color: "#fff", zIndex: 10, "&:hover": { bgcolor: "rgba(0,0,0,0.8)" } }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={image || "/assets/images/placeholder_bg.png"}
            alt={name}
            sx={{ display: "block", maxWidth: "90vw", maxHeight: "85vh", objectFit: "contain" }}
          />
        </Box>
      </Modal>

      <Card
        onClick={() => navi(`/syndicate-plots/details/${id}`)}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          borderRadius: 4,
          backgroundImage: "url('/assets/images/syndicate_bg_1.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          p: { xs: 1, sm: 1.5 },
          overflow: "hidden",
          width: "100%",
          mx: "auto",
          gap: { xs: 1, sm: 2 },
          backgroundColor: "#FBFBF8",
          cursor: "pointer",
          transition: "transform 0.22s ease, box-shadow 0.22s ease",
          "&:hover": {
            transform: "translateY(-4px) scale(1.01)",
            boxShadow: "0 10px 28px rgba(0,0,0,0.14)",
          },
        }}
      >
        {/* LEFT IMAGE */}
        <Box onClick={(e) => e.stopPropagation()} sx={{ position: "relative" }}>
          {isSold && (
            <Box
              sx={{
                position: "absolute",
                top: 12,
                left: 12,
                bgcolor: "#d32f2f",
                color: "#fff",
                px: 2,
                py: 0.6,
                borderRadius: 1.5,
                fontSize: 14,
                fontWeight: 800,
                zIndex: 3,
                textTransform: "uppercase",
                boxShadow: "0 4px 12px rgba(0,0,0,0.45)",
                letterSpacing: "0.5px",
              }}
            >
              Sold Out
            </Box>
          )}
          <Box
            sx={{
              opacity: isSold ? 0.6 : 1,
              filter: isSold ? "grayscale(20%)" : "none",
            }}
          >
            <CardMedia
              component="img"
              onClick={(e) => { e.stopPropagation(); setImgOpen(true); }}
              sx={{
                width: { xs: "100%", sm: 320, md: 240, lg: 320 },
                height: { xs: 180, sm: 320, md: 300, lg: 330 },
                objectFit: "cover",
                borderRadius: 3,
                flexShrink: 0,
                cursor: "zoom-in",
                transition: "transform 0.3s ease",
                "&:hover": { transform: "scale(1.04)" },
              }}
              image={image || "/assets/images/placeholder_bg.png"}
              alt="Plot"
            />
          </Box>
        </Box>

        {/* RIGHT CONTENT */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            p: 2,
            opacity: isSold ? 0.65 : 1,
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {/* PROPERTY NAME */}
            <Typography
              variant="h6"
              color="text.primary"
              sx={{
                fontWeight: { xs: 500, sm: 600, md: 500, lg: 800 },
                fontSize: { xs: "1rem", sm: "1.2rem", md: "1rem", lg: "1.3rem" },
              }}
            >
              {name || ""}
            </Typography>

            {/* ADDRESS */}
            <Box display="flex" alignItems="center" mt={0.5} mb={1}>
              <LocationOn
                sx={{
                  fontSize: { xs: "1rem", sm: "1.2rem", md: "0.8rem", lg: "1.8rem" },
                  color: "#9DC354",
                  mr: 0.5,
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {address || ""}
              </Typography>
            </Box>

            <Divider
              sx={{
                borderStyle: "dashed",
                borderColor: "secondary.light",
                borderWidth: 1,
                display: { xs: "block", sm: "block" },
                my: 1,
              }}
            />

            {/* PRICE & AREA */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1,
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              {/* PRICE PER SQFT */}
              <Box sx={{ flex: 2, minWidth: "120px" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}>
                  <CurrencyRupeeIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: "#9DC354" }} />
                  <Typography sx={{ fontWeight: 700, fontSize: { xs: 13, sm: 14, md: 15, lg: 16 } }}>
                    {price || "-"}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: 11, sm: 12, md: 13 }, color: "text.secondary" }}>
                    /Sq.ft
                  </Typography>
                </Box>
                <Typography sx={{ mt: 0.3, fontSize: { xs: 11, sm: 12, md: 13 }, color: "text.secondary" }}>
                  Per Sq.ft
                </Typography>
              </Box>

              {/* AREA */}
              <Box sx={{ flex: 1, minWidth: "100px" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}>
                  <OpenWithIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: "#9DC354" }} />
                  <Typography sx={{ fontSize: { xs: 12, sm: 14, md: 15, lg: 16 }, fontWeight: 600 }}>
                    {area || "-"}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: 11, sm: 12, md: 13 }, color: "text.secondary" }}>
                    Sq.ft
                  </Typography>
                </Box>
                <Typography sx={{ mt: 0.3, fontSize: { xs: 11, sm: 12, md: 13 }, color: "text.secondary" }}>
                  Total Area
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ borderStyle: "dashed", borderColor: "secondary.light", borderWidth: 1, my: 1 }} />

            {/* NEARBY */}
            <Box mt={1}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
                Near By:&nbsp;
                <Typography component="span" variant="body2" color="text.secondary">
                  {nearByList.length > 0
                    ? nearByList.map((item, index) => (
                      <span key={item.id}>
                        {item.title}
                        {index !== nearByList.length - 1 && " • "}
                      </span>
                    ))
                    : "No Data Found"}
                </Typography>
              </Typography>
            </Box>

            {/* AMENITIES */}
            <Box mt={1}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
                Amenities:&nbsp;
                <Typography component="span" variant="body2" color="text.secondary">
                  {amenityList.length > 0
                    ? amenityList.map((item, index) => (
                      <span key={item.id}>
                        {item.title}
                        {index !== amenityList.length - 1 && " • "}
                      </span>
                    ))
                    : "No Data Found"}
                </Typography>
              </Typography>
            </Box>
          </CardContent>

          {/* VIEW DETAILS BUTTON */}
          <Box display="flex" justifyContent={{ xs: "center", sm: "flex-start" }} mt={2}>
            <Button
              variant="contained"
              sx={{
                width: { xs: "100%", sm: 140 },
                maxWidth: 180,
                height: 36,
                textTransform: "none",
                bgcolor: "#FDBA15",
                color: "black",
                "&:hover": { bgcolor: "#e3a30f" },
                borderRadius: 20,
                fontWeight: 500,
                fontSize: 14,
              }}
              onClick={(e) => { e.stopPropagation(); navi(`/syndicate-plots/details/${id}`); }}
            >
              View Details
            </Button>
          </Box>
        </Box>
      </Card>
    </>
  );
};

export default Syndicate_Card;
